// Core Modules
import { HttpClient, HttpContext } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

// External Modules
import { NgxPermissionsService } from 'ngx-permissions';
import { catchError, map, Observable, of } from 'rxjs';

// Services
import { ConfigService } from '@core/services/config/config.service';
import { ErrorHandlerService } from '@core/services/error-handler/error-handler.service';

// Mappers
import { chatMapper, clientMapper, historicalPullMapper, marketMapper, tableauMapper, tokenMapper, userMapper } from '@core/permission-mappers/permission-mapper';

// Tokens
import { BACKGROUND_REFRESH } from '@core/token/tokens';

// Models
import { AppEndpointsModel } from '@shared/models/endpoints.config.model';
import { IUserDetails } from '@shared/models/auth-api-response.model';

// Enums
import { MainNavigationEnum } from '@shared/enum/main-navigation-url.enum';

// Navigation
import { areaForUrl, firstPermittedDestination, NavTarget } from '@layout/side-nav/nav.model';

@Injectable({
  providedIn: 'root',
})
export class AccessControlService {
  // Injectors
  private readonly configService = inject(ConfigService);
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly httpClient = inject(HttpClient);
  private readonly permissionsService = inject(NgxPermissionsService);
  private readonly router = inject(Router);

  // Cached /auth/me body — powers the account-dropdown badge and the My access & scopes modal with no extra request.
  private readonly _userDetails = signal<IUserDetails | null>(null);
  public readonly userDetails = this._userDetails.asReadonly();

  // The live ngx-permissions store as a signal. Derived from `permissions$` rather than mirrored on
  // every `loadPermissions()` call, so there is exactly one source of truth and nothing to keep in sync.
  private readonly permissions = toSignal(this.permissionsService.permissions$, { initialValue: this.permissionsService.getPermissions() });

  /** First nav destination the user's scopes admit, in registry order; null when they admit none. */
  public readonly landingDestination = computed<NavTarget | null>(() => firstPermittedDestination(this.permissions()));

  /**
   * Route to land on after login or a browser refresh. The side-nav and the route guards read the
   * same permissions, so landing anywhere else bounces scope-limited users to Access denied. A user
   * whose scopes admit nothing goes to /no-access rather than into an empty shell.
   */
  public readonly landingRoute = computed<string>(() => this.landingDestination()?.route ?? `/${MainNavigationEnum.NO_ACCESS}`);

  // Private Members
  private readonly applicationScopes: Record<string, string[]>;
  private readonly appEndpoints: AppEndpointsModel;

  constructor() {
    this.applicationScopes = { ...userMapper(), ...tokenMapper(), ...clientMapper(), ...historicalPullMapper(), ...marketMapper(), ...tableauMapper(), ...chatMapper() };
    this.appEndpoints = this.configService.getEndpointsByModule('appEndpoints');
  }

  public definePermissions(): Observable<boolean> {
    return this.getUserScopes().pipe(
      map((resp: IUserDetails): boolean => {
        this._userDetails.set(resp);
        this.permissionsService.loadPermissions(this.permissionNamesFor(resp));
        return true;
      }),
      catchError(this.errorHandlerService.errorHandler)
    );
  }

  public getUserScopes(): Observable<IUserDetails> {
    return this.httpClient.get<IUserDetails>(`${this.configService.getAPIBaseUrl}${this.appEndpoints.userScopes}`).pipe(
      map((data: IUserDetails) => data),
      catchError(this.errorHandlerService.errorHandler)
    );
  }

  /**
   * Re-read the user's scopes and republish permissions, leaving the session intact if the call
   * fails. When the change revokes the area the user is currently in — or grants a destination to a
   * user parked on /no-access — they are moved to their landing route. Safe to call on a timer: it
   * never throws and no-ops when nothing changed.
   * @returns true when the permission set changed; false when it was unchanged or the call failed
   */
  public refreshPermissions(): Observable<boolean> {
    return this.httpClient.get<IUserDetails>(`${this.configService.getAPIBaseUrl}${this.appEndpoints.userScopes}`, { context: new HttpContext().set(BACKGROUND_REFRESH, true) }).pipe(
      map((resp: IUserDetails): boolean => {
        // Cache the fresh body before the unchanged-set check: the account badge and the My access &
        // scopes modal read it, and those fields can change without any mapped permission name changing.
        this._userDetails.set(resp);
        const granted = this.permissionNamesFor(resp);
        const current = Object.keys(this.permissions());
        if (granted.length === current.length && granted.every((name) => current.includes(name))) return false;
        this.permissionsService.loadPermissions(granted);
        this.reconcileCurrentRoute();
        return true;
      }),
      catchError((error) => {
        // Deliberately swallowed: a background failure must not disturb a working session. The
        // interceptor's force-logout is opted out of via BACKGROUND_REFRESH for the same reason.
        console.log('<AccessControlService> - <refreshPermissions> - background /auth/me failed, keeping the current permissions', error);
        return of(false);
      })
    );
  }

  /**
   * Permission names a scope payload grants — an entry is granted only when every scope it lists is present.
   * @param userDetails the /auth/me body
   * @returns matched ngx-permissions names
   */
  private permissionNamesFor(userDetails: IUserDetails): string[] {
    return Object.keys(this.applicationScopes).filter((key) => this.applicationScopes[key].every((value: string) => userDetails.scopes.includes(value)));
  }

  /**
   * Keep the current route consistent with the permission set after a refresh — in both directions.
   * A revoked area moves the user to their landing route; a newly-granted destination moves them off
   * the no-access page, which is the one screen whose whole audience is waiting for a grant.
   * Area-level only: a deeper gate (e.g. `client-private-write` on the client edit form) is enforced
   * by that route's own guard on the next navigation, because forcing a same-URL reload here would
   * destroy unsaved form state.
   */
  private reconcileCurrentRoute(): void {
    const area = areaForUrl(this.router.url);
    if (area && !this.permissions()[area.permission]) {
      this.router.navigateByUrl(this.landingRoute());
      return;
    }
    if (!area && this.landingDestination() && this.router.url.split(/[?#]/)[0] === `/${MainNavigationEnum.NO_ACCESS}`) {
      this.router.navigateByUrl(this.landingRoute());
    }
  }
}
